import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = [
  "#6366F1", // Indigo
  "#8B5CF6", // Violet
  "#EC4899", // Pink
  "#F43F5E", // Rose
  "#F97316", // Orange
  "#F59E0B", // Amber
  "#84CC16", // Lime
  "#10B981", // Emerald
  "#14B8A6", // Teal
  "#3B82F6", // Blue
];

const AddGroupModal = ({ open, onClose, onSubmit, editingGroup = null }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [loading, setLoading] = useState(false);

  const isEditing = !!editingGroup;

  useEffect(() => {
    if (editingGroup) {
      setName(editingGroup.name || "");
      setColor(editingGroup.color || PRESET_COLORS[0]);
    } else {
      setName("");
      setColor(PRESET_COLORS[0]);
    }
  }, [editingGroup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Group name is required");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        id: editingGroup?.id,
        name: name.trim(),
        color,
      });
      if (!isEditing) {
        setName("");
        setColor(PRESET_COLORS[0]);
      }
    } catch (error) {
      console.error("Submit error:", error);
    }
    setLoading(false);
  };

  const handleClose = () => {
    setName("");
    setColor(PRESET_COLORS[0]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[400px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEditing ? "Edit Group" : "New Group"}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the group name and color" : "Organize your servers into groups"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              data-testid="group-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Production"
              className="h-11 rounded-xl"
              required
              autoFocus
            />
          </div>

          <div className="space-y-3">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((presetColor) => (
                <button
                  key={presetColor}
                  type="button"
                  data-testid={`color-btn-${presetColor.replace('#', '')}`}
                  className={`w-9 h-9 rounded-xl transition-all ${
                    color === presetColor
                      ? "ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: presetColor }}
                  onClick={() => setColor(presetColor)}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 border border-border">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className="font-medium">{name || "Group Name"}</span>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={handleClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-xl gap-2" data-testid="submit-group-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEditing ? "Save" : "Create Group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddGroupModal;
